import { useState, useEffect, useCallback } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Mail, Copy, RefreshCw, Inbox, Check, X, AlertTriangle, Zap } from 'lucide-react';

/**
 * AppFolio Import section embedded in the Integrations tab.
 *
 * - Shows the per-portfolio inbound email address (generated server-side)
 * - Lets owners copy it / regenerate it
 * - Lists pending imports (badge count is bubbled up via onPendingCount)
 * - Opens a preview modal for each pending import showing the structured diff
 *   with Approve / Reject actions
 */
export default function AppFolioImport({ portfolioId, onPendingCount }) {
  const [emailInfo, setEmailInfo] = useState(null);
  const [pending, setPending] = useState([]);
  const [auditItems, setAuditItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [actioning, setActioning] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [savingAutoApply, setSavingAutoApply] = useState(false);

  const loadAll = useCallback(async () => {
    if (!portfolioId) return;
    setLoading(true);
    try {
      const [emailRes, pendingRes, auditRes] = await Promise.all([
        api.get(`/portfolios/${portfolioId}/import-email`),
        api.get(`/imports/pending?portfolio_id=${portfolioId}`),
        api.get(`/imports/audit?portfolio_id=${portfolioId}`),
      ]);
      setEmailInfo(emailRes.data);
      const items = pendingRes.data?.imports || [];
      setPending(items);
      setAuditItems(auditRes.data?.audit || []);
      if (typeof onPendingCount === 'function') onPendingCount(items.length);
    } catch (e) {
      // Non-owners may get 403 on regenerate but get on get-import-email is allowed
      console.error('Failed to load AppFolio import data', e);
    } finally {
      setLoading(false);
    }
  }, [portfolioId, onPendingCount]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleCopy = () => {
    if (!emailInfo?.address) return;
    navigator.clipboard.writeText(emailInfo.address).then(
      () => toast.success('Email address copied to clipboard'),
      () => toast.error('Could not copy address')
    );
  };

  const handleRegenerate = async () => {
    if (!portfolioId) return;
    if (!window.confirm('Generate a new inbound address? The old one will stop working immediately.')) return;
    setRegenerating(true);
    try {
      const res = await api.post(`/portfolios/${portfolioId}/import-email/regenerate`);
      setEmailInfo((prev) => ({ ...prev, ...res.data }));
      toast.success('New inbound address generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to regenerate address');
    } finally {
      setRegenerating(false);
    }
  };

  const handleAutoApplyToggle = async (next) => {
    if (!portfolioId || savingAutoApply) return;
    if (
      next &&
      !window.confirm(
        'Turn ON auto-apply?\n\nIncoming AppFolio CSVs will be applied to your portfolio immediately, with no review step. Imports that contain parsing errors or duplicate units will still fall back to manual review.'
      )
    ) {
      return;
    }
    setSavingAutoApply(true);
    // Optimistic update
    setEmailInfo((prev) => (prev ? { ...prev, auto_apply: next } : prev));
    try {
      const res = await api.patch(`/portfolios/${portfolioId}/import-settings`, { auto_apply: next });
      setEmailInfo((prev) => ({ ...prev, ...res.data }));
      toast.success(next ? 'Auto-apply turned ON' : 'Auto-apply turned OFF');
    } catch (e) {
      // Revert on failure
      setEmailInfo((prev) => (prev ? { ...prev, auto_apply: !next } : prev));
      toast.error(e.response?.data?.detail || 'Failed to update setting');
    } finally {
      setSavingAutoApply(false);
    }
  };

  const openPreview = async (item) => {
    setPreviewLoading(true);
    setPreviewItem(item); // optimistic — show modal with what we have
    try {
      const res = await api.get(`/imports/${item.id}`);
      setPreviewItem(res.data);
    } catch (e) {
      toast.error('Failed to load import details');
      setPreviewItem(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (actioning) return;
    setPreviewItem(null);
  };

  const handleApprove = async () => {
    if (!previewItem) return;
    if (!window.confirm('Approve this import? Live unit data will be replaced with the parsed CSV.')) return;
    setActioning(true);
    try {
      const res = await api.post(`/imports/${previewItem.id}/approve`);
      toast.success(`Import applied: ${res.data?.applied?.inserted ?? 0} units`);
      setPreviewItem(null);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve import');
    } finally {
      setActioning(false);
    }
  };

  const handleReject = async () => {
    if (!previewItem) return;
    if (!window.confirm('Reject this import? It will be discarded.')) return;
    setActioning(true);
    try {
      await api.post(`/imports/${previewItem.id}/reject`);
      toast.success('Import rejected');
      setPreviewItem(null);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject import');
    } finally {
      setActioning(false);
    }
  };

  return (
    <div className="dashboard-section" data-testid="appfolio-import-section">
      <div className="section-header">
        <h2>
          <Inbox size={18} style={{ display: 'inline', marginRight: 8, verticalAlign: 'text-bottom' }} />
          AppFolio Scheduled-Report Import
        </h2>
        {pending.length > 0 && (
          <span
            className="status-badge"
            style={{ background: '#f59e0b', color: '#fff' }}
            data-testid="pending-imports-badge"
          >
            {pending.length} pending
          </span>
        )}
      </div>

      <p style={{ color: '#64748b', fontSize: 14, marginBottom: 16 }}>
        Configure your AppFolio scheduled rent-roll report to email a CSV to the address below. We&rsquo;ll
        parse it and stage it for your review before applying any changes.
      </p>

      {/* Inbound address */}
      <div
        className="sheet-info"
        style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
        data-testid="inbound-email-display"
      >
        <Mail size={16} />
        <code
          style={{
            fontSize: 14,
            background: '#f1f5f9',
            padding: '6px 10px',
            borderRadius: 6,
            flex: '1 1 auto',
            minWidth: 240,
            wordBreak: 'break-all',
          }}
          data-testid="inbound-email-address"
        >
          {emailInfo?.address || (loading ? 'Loading…' : 'Not generated yet')}
        </code>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopy}
          disabled={!emailInfo?.address}
          data-testid="copy-inbound-email-button"
        >
          <Copy size={14} style={{ marginRight: 6 }} /> Copy
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRegenerate}
          disabled={regenerating || !portfolioId}
          data-testid="regenerate-inbound-email-button"
        >
          <RefreshCw size={14} style={{ marginRight: 6 }} />
          {regenerating ? 'Regenerating…' : 'Regenerate'}
        </Button>
      </div>

      {emailInfo?.last_received_at && (
        <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
          Last received: {new Date(emailInfo.last_received_at).toLocaleString()}
        </p>
      )}

      {/* Auto-apply toggle */}
      <div
        data-testid="auto-apply-row"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          padding: '12px 14px',
          background: emailInfo?.auto_apply ? '#fef3c7' : '#f8fafc',
          border: `1px solid ${emailInfo?.auto_apply ? '#fcd34d' : '#e2e8f0'}`,
          borderRadius: 8,
          marginBottom: 16,
        }}
      >
        <div style={{ flex: '1 1 auto' }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Zap size={14} />
            Auto-apply imports
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            When ON, incoming AppFolio CSVs are applied to your portfolio
            immediately with no review step. Imports with parsing errors or
            duplicate units still require manual review.
          </div>
        </div>
        <Switch
          checked={!!emailInfo?.auto_apply}
          disabled={savingAutoApply || !portfolioId || !emailInfo}
          onCheckedChange={handleAutoApplyToggle}
          data-testid="auto-apply-toggle"
        />
      </div>

      {/* Pending imports list */}
      <div data-testid="pending-imports-list">
        <h3 style={{ fontSize: 16, marginBottom: 8 }}>Pending Imports</h3>
        {pending.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: 14, fontStyle: 'italic' }}>
            No imports waiting for review.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pending.map((item) => {
              const summary = item.diff?.summary || {};
              return (
                <div
                  key={item.id}
                  className="pending-import-row"
                  style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    padding: 12,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                  data-testid={`pending-import-${item.id}`}
                >
                  <div style={{ flex: '1 1 auto', minWidth: 240 }}>
                    <div style={{ fontWeight: 600 }}>
                      {item.filename || 'attachment.csv'}{' '}
                      <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 13 }}>
                        from {item.sender || 'unknown'}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                      Received {new Date(item.received_at).toLocaleString()} ·{' '}
                      {item.parsed_rows_count || 0} rows ·{' '}
                      <span style={{ color: '#10b981' }}>+{summary.added_count || 0}</span>{' '}
                      <span style={{ color: '#ef4444' }}>-{summary.removed_count || 0}</span>{' '}
                      <span style={{ color: '#3b82f6' }}>~{summary.status_changed_count || 0} status</span>
                      {summary.error_count > 0 && (
                        <>
                          {' · '}
                          <span style={{ color: '#dc2626' }}>
                            <AlertTriangle size={12} style={{ verticalAlign: 'text-bottom' }} />{' '}
                            {summary.error_count} errors
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={() => openPreview(item)}
                    data-testid={`review-import-button-${item.id}`}
                  >
                    Review
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Audit log toggle */}
      <div style={{ marginTop: 24 }}>
        <button
          type="button"
          onClick={() => setShowAudit((v) => !v)}
          style={{
            background: 'none',
            border: 'none',
            color: '#3b82f6',
            cursor: 'pointer',
            fontSize: 13,
            padding: 0,
          }}
          data-testid="toggle-audit-log-button"
        >
          {showAudit ? 'Hide' : 'Show'} import history ({auditItems.length})
        </button>
        {showAudit && (
          <div
            style={{ marginTop: 8, maxHeight: 240, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}
            data-testid="audit-log-list"
          >
            {auditItems.length === 0 ? (
              <p style={{ padding: 12, color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>
                No history yet.
              </p>
            ) : (
              auditItems.map((a) => (
                <div
                  key={a.id}
                  style={{
                    padding: '8px 12px',
                    fontSize: 13,
                    borderBottom: '1px solid #f1f5f9',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <span>
                    <strong style={{ textTransform: 'capitalize' }}>{a.action}</strong>{' '}
                    {a.filename && <span style={{ color: '#64748b' }}>· {a.filename}</span>}
                    {a.actor && <span style={{ color: '#64748b' }}> · by {a.actor}</span>}
                  </span>
                  <span style={{ color: '#94a3b8' }}>
                    {new Date(a.performed_at || a.received_at).toLocaleString()}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Preview modal */}
      <Dialog open={!!previewItem} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="import-preview-modal"
        >
          <DialogHeader>
            <DialogTitle>Review Import</DialogTitle>
            <DialogDescription>
              Review the changes parsed from the AppFolio CSV. Approve to apply, or reject to discard.
            </DialogDescription>
          </DialogHeader>

          {previewLoading && !previewItem?.diff ? (
            <p style={{ padding: 16 }}>Loading preview…</p>
          ) : previewItem ? (
            <ImportDiffView item={previewItem} />
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleReject}
              disabled={actioning}
              data-testid="reject-import-button"
            >
              <X size={14} style={{ marginRight: 6 }} />
              Reject
            </Button>
            <Button
              onClick={handleApprove}
              disabled={actioning}
              data-testid="approve-import-button"
            >
              <Check size={14} style={{ marginRight: 6 }} />
              {actioning ? 'Applying…' : 'Approve & Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ImportDiffView({ item }) {
  const diff = item.diff || {};
  const summary = diff.summary || {};
  const sections = [
    { key: 'added', label: 'Added units', color: '#10b981', items: diff.added || [], render: renderAddRemove },
    { key: 'removed', label: 'Removed units', color: '#ef4444', items: diff.removed || [], render: renderAddRemove },
    { key: 'status_changed', label: 'Status changes', color: '#3b82f6', items: diff.status_changed || [], render: renderChange },
    { key: 'rent_changed', label: 'Rent changes', color: '#8b5cf6', items: diff.rent_changed || [], render: renderChange },
    { key: 'bd_ba_changed', label: 'BD/BA changes', color: '#f59e0b', items: diff.bd_ba_changed || [], render: renderChange },
    { key: 'sqft_changed', label: 'Sqft changes', color: '#0891b2', items: diff.sqft_changed || [], render: renderChange },
  ];

  return (
    <div data-testid="import-diff-view">
      <div style={{ marginBottom: 16, fontSize: 13, color: '#64748b' }}>
        <strong>{item.filename || 'attachment.csv'}</strong> from {item.sender || 'unknown'} ·{' '}
        Received {new Date(item.received_at).toLocaleString()}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 8,
          marginBottom: 16,
        }}
      >
        <SummaryStat label="Parsed rows" value={summary.total_parsed_rows} />
        <SummaryStat label="Added" value={summary.added_count} color="#10b981" />
        <SummaryStat label="Removed" value={summary.removed_count} color="#ef4444" />
        <SummaryStat label="Status changes" value={summary.status_changed_count} color="#3b82f6" />
        <SummaryStat label="Rent changes" value={summary.rent_changed_count} color="#8b5cf6" />
        <SummaryStat label="Errors" value={summary.error_count} color="#dc2626" />
      </div>

      {(diff.errors || []).length > 0 && (
        <DiffBlock label="Parsing errors" color="#dc2626" testid="diff-errors">
          {(diff.errors || []).map((e, i) => (
            <div key={i} style={{ fontSize: 13, padding: '4px 0' }}>
              Row {e.row_number}: {e.message}
            </div>
          ))}
        </DiffBlock>
      )}

      {(diff.duplicates || []).length > 0 && (
        <DiffBlock label="Duplicate units in CSV" color="#f59e0b" testid="diff-duplicates">
          {(diff.duplicates || []).map((d, i) => (
            <div key={i} style={{ fontSize: 13, padding: '4px 0' }}>
              {d.address} — Unit {d.unit} (row {d.row_number})
            </div>
          ))}
        </DiffBlock>
      )}

      {(diff.missing_fields || []).length > 0 && (
        <DiffBlock label="Rows with missing fields" color="#f59e0b" testid="diff-missing">
          {(diff.missing_fields || []).map((m, i) => (
            <div key={i} style={{ fontSize: 13, padding: '4px 0' }}>
              {m.address} — Unit {m.unit}: missing {m.missing.join(', ')}
            </div>
          ))}
        </DiffBlock>
      )}

      {sections.map((s) =>
        s.items.length > 0 ? (
          <DiffBlock key={s.key} label={`${s.label} (${s.items.length})`} color={s.color} testid={`diff-${s.key}`}>
            {s.items.slice(0, 200).map((it, i) => (
              <div key={i} style={{ fontSize: 13, padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                {s.render(it)}
              </div>
            ))}
            {s.items.length > 200 && (
              <div style={{ fontSize: 12, color: '#94a3b8', padding: 4 }}>
                + {s.items.length - 200} more…
              </div>
            )}
          </DiffBlock>
        ) : null
      )}

      {sections.every((s) => s.items.length === 0) &&
        (diff.errors || []).length === 0 &&
        (diff.duplicates || []).length === 0 &&
        (diff.missing_fields || []).length === 0 && (
          <p style={{ color: '#94a3b8', fontStyle: 'italic' }}>
            No changes detected — approving will leave the portfolio unchanged.
          </p>
        )}
    </div>
  );
}

function SummaryStat({ label, value, color }) {
  return (
    <div
      style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: 10,
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 20, fontWeight: 700, color: color || '#0f172a' }}>{value || 0}</div>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
    </div>
  );
}

function DiffBlock({ label, color, children, testid }) {
  return (
    <div style={{ marginBottom: 12 }} data-testid={testid}>
      <div
        style={{
          fontWeight: 600,
          fontSize: 14,
          color,
          marginBottom: 4,
          paddingBottom: 4,
          borderBottom: `2px solid ${color}33`,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function renderAddRemove(it) {
  return (
    <span>
      {it.address} — <strong>Unit {it.unit}</strong>
      {it.status ? ` · ${it.status}` : ''}
      {it.bd_ba ? ` · ${it.bd_ba}` : ''}
    </span>
  );
}

function renderChange(it) {
  return (
    <span>
      {it.address} — <strong>Unit {it.unit}</strong>:{' '}
      <code style={{ background: '#fef2f2', padding: '1px 4px', borderRadius: 3 }}>{it.from || '—'}</code>
      {' → '}
      <code style={{ background: '#f0fdf4', padding: '1px 4px', borderRadius: 3 }}>{it.to || '—'}</code>
    </span>
  );
}
